import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';

export interface BattlepassRewardCell {
  user_reward_id: number;
  CodeName128: string;
  ispremium: number;
  claimed: number;
  level: number;
  SN_Name: string;
  image_url: string;
  Quantity: number;
  reward_type: string;
  state: string;
  is_default?: number;
  gender?: string;
  has_multiple?: boolean;
  group_reward_ids?: number[];
  group_items?: BattlepassRewardCell[];
}

export interface BattlepassStaticData {
  battlepass: {
    id: number;
    battlepass_id?: string | number;
    name: string;
    start_date: string;
    end_date: string;
    banner_img: string | null;
    reward_img: string | null;
    updated_at?: string | null;
    banner_rev?: number | null;
  } | null;
  maxLevels: number;
  freeRewards: Record<string, BattlepassRewardCell>;
  premiumRewards: Record<string, BattlepassRewardCell>;
}

export interface BattlepassProgressData {
  userLevel: number;
  userExp: number;
  userIsPremium: number;
  maxLevels: number;
  userGender?: string | null;
  freeRewards: Record<string, BattlepassRewardCell>;
  premiumRewards: Record<string, BattlepassRewardCell>;
}

export interface BattlepassData {
  battlepass: BattlepassStaticData['battlepass'];
  userLevel: number;
  userExp: number;
  userIsPremium: number;
  maxLevels: number;
  userGender?: string | null;
  freeRewards: Record<string, BattlepassRewardCell>;
  premiumRewards: Record<string, BattlepassRewardCell>;
  fromCache?: boolean;
}

export const BATTLEPASS_STATIC_KEY = ['battlepass', 'static'] as const;
export const BATTLEPASS_PROGRESS_KEY = ['battlepass', 'progress'] as const;

export async function fetchBattlepassStatic(): Promise<BattlepassStaticData> {
  const { data } = await api.get<BattlepassStaticData & { status: string }>('/api/v1/battlepass/static');
  return data;
}

export async function fetchBattlepassProgress(): Promise<BattlepassProgressData | null> {
  const { data } = await api.get<{ status: string; progress: BattlepassProgressData | null }>(
    '/api/v1/battlepass/progress',
  );
  return data.progress;
}

export function useBattlepassStatic() {
  return useQuery({
    queryKey: BATTLEPASS_STATIC_KEY,
    queryFn: fetchBattlepassStatic,
    staleTime: 10_000,
    gcTime: 5 * 60_000,
    refetchOnMount: 'always',
  });
}

export function useBattlepassProgress() {
  return useQuery({
    queryKey: BATTLEPASS_PROGRESS_KEY,
    queryFn: fetchBattlepassProgress,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** @deprecated Prefer useBattlepassStatic + useBattlepassProgress */
export function useBattlepass() {
  return useQuery({
    queryKey: ['battlepass'],
    queryFn: async () => {
      const { data } = await api.get<BattlepassData & { status: string }>('/api/v1/battlepass');
      return data;
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useBattlepassMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: BATTLEPASS_PROGRESS_KEY });
    queryClient.invalidateQueries({ queryKey: ['battlepass'] });
  };

  const claimReward = async (rewardId: number) => {
    const { data } = await api.post<{ success: boolean; message: string }>('/api/v1/battlepass/claim', {
      reward_id: rewardId,
    });
    return data;
  };

  const claim = useMutation({
    mutationFn: claimReward,
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => invalidate(), 750);
      } else {
        invalidate();
      }
    },
  });

  const claimMany = async (rewardIds: number[]) => {
    const results = await Promise.all(rewardIds.map((id) => claimReward(id)));
    const allSuccess = results.length > 0 && results.every((r) => r.success);
    if (allSuccess) {
      window.setTimeout(() => invalidate(), 750);
    } else {
      invalidate();
    }
    return { results, allSuccess };
  };

  const buyPremium = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ success: boolean; message: string }>('/api/v1/battlepass/buy-premium');
      return data;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['silk-balance'] });
    },
  });

  const levelUp = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ success: boolean; message: string }>('/api/v1/battlepass/level-up');
      return data;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['silk-balance'] });
    },
  });

  return { claim, claimMany, buyPremium, levelUp };
}

export function prefetchBattlepassStatic(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.prefetchQuery({
    queryKey: BATTLEPASS_STATIC_KEY,
    queryFn: fetchBattlepassStatic,
    staleTime: 10_000,
  });
}
